/**
 * JSON check rules (spec §12.1): a small condition language + title templates.
 * Pure — `now` is passed in.
 */
import { isEmptyValue, fmtMoney, fmtNumber, fmtPercent, toNumber } from "../format/registry";
import { addDays, displayDate, isBefore, monthsBetween, nowFields, parseDateLike } from "../format/dates";

/** Read "a.b[2].c" out of an object. */
export const getPath = (obj, path) => {
  if (!path) return obj;
  const parts = String(path).match(/[^.[\]]+/g) || [];
  let cur = obj;
  for (const p of parts) {
    if (cur === null || cur === undefined) return undefined;
    cur = cur[/^\d+$/.test(p) ? Number(p) : p];
  }
  return cur;
};

const resolve = (path, scope) =>
  String(path).startsWith("derived.")
    ? getPath(scope.derived, String(path).slice("derived.".length))
    : getPath(scope.row ?? scope.payload, path);

const compare = (a, b, op) => {
  const na = toNumber(a);
  const nb = toNumber(b);
  const [x, y] = na !== null && nb !== null ? [na, nb] : [a, b];
  switch (op) {
    case "eq": return x === y || String(x).toLowerCase() === String(y).toLowerCase();
    case "neq": return !(x === y || String(x).toLowerCase() === String(y).toLowerCase());
    case "lt": return x < y;
    case "lte": return x <= y;
    case "gt": return x > y;
    case "gte": return x >= y;
    default: return false;
  }
};

/**
 * @returns {{ ok: boolean, count?: number }}
 */
export const evalCondition = (cond, scope) => {
  if (!cond) return { ok: false };
  if (cond.all) return { ok: cond.all.every((c) => evalCondition(c, scope).ok) };
  if (cond.any) return { ok: cond.any.some((c) => evalCondition(c, scope).ok) };
  const v = resolve(cond.path, scope);
  switch (cond.op) {
    case "empty": return { ok: isEmptyValue(v) };
    case "notEmpty": return { ok: !isEmptyValue(v), count: Array.isArray(v) ? v.length : undefined };
    case "true": return { ok: v === true };
    case "false": return { ok: v === false };
    case "eq": case "neq": case "lt": case "lte": case "gt": case "gte":
      return { ok: v !== null && v !== undefined && compare(v, cond.value, cond.op) };
    case "in":
      return {
        ok:
          v !== null &&
          v !== undefined &&
          (cond.value || []).some((x) => String(x).toLowerCase() === String(v).toLowerCase()),
      };
    case "matches":
      return { ok: typeof v === "string" && new RegExp(cond.value, "i").test(v) };
    case "before":
    case "after": {
      const d = parseDateLike(typeof v === "string" ? v : "");
      if (!d) return { ok: false };
      let ref = cond.value === "now" ? nowFields(scope.now) : parseDateLike(cond.value);
      if (!ref) return { ok: false };
      if (cond.offsetDays) ref = addDays(ref, cond.offsetDays);
      return { ok: cond.op === "before" ? isBefore(d, ref) : isBefore(ref, d) };
    }
    case "anyRow": {
      if (!Array.isArray(v)) return { ok: false, count: 0 };
      const count = v.filter((row) => evalCondition(cond.where, { ...scope, row }).ok).length;
      return { ok: count > 0, count };
    }
    default:
      return { ok: false };
  }
};

const FORMATTERS = {
  money: (v) => (toNumber(v) === null ? String(v ?? "") : fmtMoney(toNumber(v))),
  number: (v) => (toNumber(v) === null ? String(v ?? "") : fmtNumber(toNumber(v))),
  percent: (v) => (toNumber(v) === null ? String(v ?? "") : fmtPercent(toNumber(v))),
  date: (v) => {
    const d = parseDateLike(String(v ?? ""));
    return d ? displayDate(d) : String(v ?? "");
  },
  monthsAgo: (v, _a, scope) => {
    const d = parseDateLike(String(v ?? ""));
    if (!d) return "";
    const n = monthsBetween(d, nowFields(scope.now));
    return `${n} ${n === 1 ? "month" : "months"}`;
  },
  plural: (v, args) => {
    const [one, many] = String(args || "").split(",");
    return Number(v) === 1 ? one : many ?? one;
  },
};

/** "{count} {count|plural:section is,sections are} high risk" */
export const renderTemplate = (tpl, scope) =>
  String(tpl ?? "").replace(/\{([^}|]+)(?:\|([a-zA-Z]+)(?::([^}]*))?)?\}/g, (_, path, fmt, args) => {
    const v = path === "count" ? scope.count : resolve(path.trim(), scope);
    if (fmt && FORMATTERS[fmt]) return FORMATTERS[fmt](v, args, scope);
    return v === null || v === undefined ? "" : String(v);
  });

/** Evaluate config rules into checks. */
export const evalRules = (rules, scope) =>
  (rules || []).flatMap((rule) => {
    const res = evalCondition(rule.when, scope);
    if (!res.ok) return [];
    const s = { ...scope, count: res.count };
    return [
      {
        id: rule.id,
        tone: rule.tone,
        title: renderTemplate(rule.title, s),
        detail: rule.detail ? renderTemplate(rule.detail, s) : undefined,
        target: rule.target,
      },
    ];
  });
